import React from 'react';
import { PageSection, SiteData } from '../../../types';
import { Building2, Sparkles } from 'lucide-react';
import { isDarkBackground, getCardStylesForBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
}

export const ReferencesSection: React.FC<SectionProps> = ({ section, siteData }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'REFERANSLARIMIZ';
  const title = content.title || 'Güvenilir İş Ortaklarımız & Markalar';
  const settingsList = (siteData.settings as any)?.referencesList || (siteData.settings as any)?.references;
  const rawItems = content.items || (Array.isArray(settingsList) ? settingsList : []);
  const items = Array.isArray(rawItems) ? rawItems : [];

  const bgColor = style.backgroundColor || '#FFFFFF';
  const isDark = isDarkBackground(bgColor);
  const cardStyles = getCardStylesForBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const anchorId = style.anchorId || style.customId || 'references';

  return (
    <section
      id={anchorId}
      data-section-id={section.id}
      data-section-type="REFERENCES"
      className="py-16 px-4 sm:px-6 lg:px-8 border-y border-black/5 select-none transition-colors"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-8 text-center">
        <div className="space-y-2">
          {badge && (
            <div
              className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-black tracking-widest uppercase border"
              style={{
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}10`,
                color: accentColor,
              }}
            >
              <Sparkles className="w-3 h-3" />
              <span>{badge}</span>
            </div>
          )}
          <h3 className={`text-xl sm:text-2xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h3>
        </div>

        {items.length === 0 ? (
          <div className="p-8 text-center rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 max-w-sm mx-auto text-xs text-slate-400">
            Henüz referans veya marka eklenmedi.
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {items.map((ref: any, idx: number) => {
              const displayName = ref.name || ref.title || `Referans ${idx + 1}`;
              return (
                <div
                  key={ref.id || idx}
                  className={`flex items-center gap-3 px-5 py-3 rounded-2xl border shadow-2xs hover:shadow-md transition-all duration-300 ${cardStyles.bg} ${cardStyles.border}`}
                >
                  {ref.logo ? (
                    <img
                      src={ref.logo}
                      alt={displayName}
                      className="h-8 max-w-[120px] object-contain filter grayscale hover:grayscale-0 transition"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <Building2 className="w-5 h-5 text-blue-500 shrink-0" />
                  )}
                  <span className={`text-sm sm:text-base font-bold tracking-tight ${cardStyles.heading}`}>
                    {displayName}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

